import {
  useLayoutEffect,
  type FC
} from 'react'
import {
  useNavigate,
  useSearchParams
} from 'react-router-dom';
import {
  useDispatch
} from 'react-redux';
import {
  selectUserBalance,
  useLazyBalanceQuery,
  useSessionSelector
} from '@entities/session';
import {
  PaymentMethod,
  PaymentMethodOrderSetTypeEnum,
  removeRecentlyUsedPaymentMethod,
  selectDepositPaymentMethodOrderSets,
  selectRecentlyUsedPaymentMethod,
  storeRecentlyUsedPaymentMethod,
  usePaymentsSelector
} from '@entities/payments';
import {
  useSendDataToGtm8847
} from '@features/collect-analytics';
import {
  PurchaseTwoPaneLayout
} from '@widgets/purchase-two-pane-layout';
import {
  storeCoinRedeem,
  resetCoinRedeem,
  selectCoinRedeem,
  useWithdrawCoinsPageSelector
} from '../model';
import {
  CoinRedemptionForm
} from './coin-redemption-form.component';
import {
  CoinRedemptionCheckoutForm
} from './coin-redemption-checkout-form.component';

export const WithdrawCoinsPage: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [getBalanceQuery] = useLazyBalanceQuery();
  const { sendDataToGtm } = useSendDataToGtm8847();

  const userBalance = useSessionSelector(selectUserBalance);
  const coinRedeem = useWithdrawCoinsPageSelector(selectCoinRedeem);
  const recently = usePaymentsSelector(selectRecentlyUsedPaymentMethod);
  const paymentMethods = usePaymentsSelector(selectDepositPaymentMethodOrderSets(PaymentMethodOrderSetTypeEnum.Withdrawal));

  const handleNavigateToLobby = (): void => {
    navigate('/lobby', { replace: true });
  };

  const handleNavigateToWithdrawCoins = (): void => {
    dispatch(resetCoinRedeem());
    navigate('/redeem', { replace: true });
  };

  const handleCoinRedeem = (amount: string | number): void => {
    dispatch(storeCoinRedeem(amount));
    navigate('/redeem?checkout=true', { replace: true });
  };

  const handleStart = (amount: string | number): void => {
    const dataLayer = {
      event: 'redeem_start',
      withdrawalCount: '1',
      paymentMethod: 'Trustly',
      withdrawalAmount: amount,
      withdrawalCurrency: 'USD',
    };
    sendDataToGtm({ dataLayer });
  };

  const handleSuccess = async (result: {
    redeemAmount: Maybe<number>;
    paymentMethod: Maybe<PaymentMethod>;
  }): Promise<void> => {
    await getBalanceQuery();

    if (result?.paymentMethod)
      dispatch(storeRecentlyUsedPaymentMethod(result?.paymentMethod));

    if (result?.redeemAmount) {
      const withdrawalAmount = result.redeemAmount;
      const paymentMethod = result.paymentMethod?.displayName ?? 'Unknown';
      const event = 'redeem_success';
      sendDataToGtm({
        dataLayer: {
          event,
          withdrawalAmount,
          paymentMethod
        }
      });
    }
  };

  const handleRedeemFail = async (result: {
    redeemAmount: Maybe<number>;
    paymentMethod: Maybe<PaymentMethod>;
  }): Promise<void> => {
    await getBalanceQuery();

    if (result?.redeemAmount) {
      const event = 'redeem_fail';
      const paymentMethod = result.paymentMethod?.displayName ?? 'Unknown';
      sendDataToGtm({ dataLayer: { event, paymentMethod } });
    }
  };

  useLayoutEffect(() => {
    const availableMethods = [
      ...(paymentMethods?.recommended ?? []),
      ...(paymentMethods?.alternative ?? []),
    ];

    const isKnownMethod = availableMethods.some(
      ({ key }) => key === recently?.key
    );

    if (!isKnownMethod) {
      dispatch(removeRecentlyUsedPaymentMethod());
    }
  }, [paymentMethods]);


  return (searchParams.get('checkout') === 'true') ? (
    <CoinRedemptionCheckoutForm
      redeemAmount={coinRedeem}
      onStart={handleStart}
      onSuccess={handleSuccess}
      onFailed={handleRedeemFail}
      onClose={handleNavigateToWithdrawCoins}
    />
  ) : (
    <PurchaseTwoPaneLayout>
      <PurchaseTwoPaneLayout.Content
        title='Redeem'
        onBack={handleNavigateToLobby}>
        <CoinRedemptionForm
          onCoinRedeem={handleCoinRedeem}
          bonusBalance={userBalance?.bonusBalanceNumber ?? 0}
          withdrawableBalance={userBalance?.withdrawableBalanceNumber ?? 0}
        />
      </PurchaseTwoPaneLayout.Content>
    </PurchaseTwoPaneLayout>
  );
};